import React, { useEffect, useState } from "react";
import { NavLink, Outlet, Navigate, useNavigate, useLocation } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useSettings } from "../../lib/useSettings";
import {
  LayoutDashboard, Wrench, Image as ImageIcon, FileText, HelpCircle, MapPin, Users,
  BarChart3, Search, Settings, LogOut, TrendingUp, Globe, LifeBuoy, Sparkles,
  FolderOpen, Link2, Network, Eye, MessagesSquare, Mail,
} from "lucide-react";

const SECTIONS = [
  {
    label: "Pilotage",
    links: [
      { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
      { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
      { to: "/admin/leads", label: "Leads", icon: Users, badgeKey: "leads" },
    ],
  },
  {
    label: "Contenu",
    links: [
      { to: "/admin/services", label: "Services", icon: Wrench },
      { to: "/admin/realisations", label: "Réalisations", icon: ImageIcon },
      { to: "/admin/blog", label: "Blog", icon: FileText },
      { to: "/admin/faq", label: "FAQ", icon: HelpCircle },
      { to: "/admin/media", label: "Médiathèque", icon: FolderOpen },
    ],
  },
  {
    label: "SEO & Acquisition",
    links: [
      { to: "/admin/zones", label: "Zones", icon: MapPin },
      { to: "/admin/rank-tracker", label: "Rank Tracker", icon: TrendingUp },
      { to: "/admin/local-seo", label: "Local SEO", icon: Sparkles },
      { to: "/admin/short-links", label: "Liens trackés", icon: Link2 },
    ],
  },
  {
    label: "Configuration",
    links: [
      { to: "/admin/support", label: "Support", icon: LifeBuoy },
      { to: "/admin/emails", label: "Historique emails", icon: Mail },
      { to: "/admin/agency-pairing", label: "HUB Agence", icon: Network },
      { to: "/admin/agent-bridge", label: "Pont Agent", icon: MessagesSquare },
      { to: "/admin/settings", label: "Paramètres", icon: Settings },
    ],
  },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { settings: s } = useSettings();
  const nav = useNavigate();
  const loc = useLocation();
  const [badges, setBadges] = useState({ leads: 0 });

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        const { data } = await api.get("/dashboard", { params: { days: 30 } });
        setBadges({ leads: data.leads_new || 0 });
      } catch {}
    };
    load();
    const iv = setInterval(load, 60_000);
    return () => clearInterval(iv);
  }, [user, loc.pathname]);

  if (!user) return <Navigate to="/admin/login" replace />;

  const company = s?.company_name || "KAP";
  const m = company.match(/^(.*?)(AIR)(.*)$/i);

  return (
    <div className="admin-scope min-h-screen flex bg-[hsl(var(--background))]">
      <aside className="w-60 lg:w-64 bg-[color:var(--brand-navy)] text-white flex-shrink-0 sticky top-0 h-screen flex flex-col overflow-hidden" data-testid="admin-sidebar">
        {/* Brand */}
        <div className="p-4 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            {s?.logo_url && <img src={s.logo_url} alt="" className="w-9 h-9 rounded-full bg-white p-0.5 object-contain" />}
            <div className="leading-tight">
              <div className="font-display font-extrabold text-sm tracking-tight">
                {m ? <>{m[1]}<span className="text-[color:var(--brand-sky)]">{m[2]}</span>{m[3]}</> : company}
              </div>
              <div className="text-[9px] text-white/40 uppercase tracking-[0.18em]">Back-office · v{s?.version || "1.1"}</div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="p-3 flex-1 overflow-y-auto">
          {SECTIONS.map((section) => (
            <div key={section.label} className="mb-4">
              <div className="px-3 mb-1.5 text-[9px] uppercase tracking-[0.22em] text-white/40 font-bold">{section.label}</div>
              <div className="space-y-0.5">
                {section.links.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-colors ${
                        isActive ? "bg-white/10 text-white shadow-[inset_2px_0_0_0_var(--brand-sky)]" : "text-white/65 hover:bg-white/5 hover:text-white"
                      }`
                    }
                    data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    <l.icon className="w-4 h-4 shrink-0" />
                    <span className="flex-1 truncate">{l.label}</span>
                    {l.badgeKey && badges[l.badgeKey] > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[color:var(--brand-red)] text-white text-[10px] font-bold" data-testid={`badge-${l.badgeKey}`}>
                        {badges[l.badgeKey] > 99 ? "99+" : badges[l.badgeKey]}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="p-3 border-t border-white/10 flex-shrink-0 space-y-1">
          <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] text-white/65 hover:text-white hover:bg-white/5">
            <Globe className="w-4 h-4" /> Voir le site
          </a>
          <button onClick={() => { logout(); nav("/admin/login"); }} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] text-white/65 hover:text-white hover:bg-white/5" data-testid="admin-logout">
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
          <div className="px-3 pt-2 text-[10px] text-white/35 truncate">{user.email}</div>
          <a
            href="https://agence-msc.fr"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition group"
            aria-label="Agence MSC"
            data-testid="admin-sidebar-msc-logo"
          >
            <div className="text-center">
              <div className="text-[8px] uppercase tracking-[0.25em] text-white/40 mb-1">Propulsé par</div>
              <img src="/uploads/logo-msc.webp" alt="Agence MSC" className="h-6 w-auto mx-auto group-hover:scale-105 transition-transform" />
            </div>
          </a>
        </div>
      </aside>

      {/* Main content — takes all remaining width */}
      <main className="flex-1 min-w-0 overflow-x-hidden bg-[hsl(var(--background))]">
        {user?.view_mode === "client" && (
          <div
            className="sticky top-0 z-50 bg-violet-600 text-white px-4 py-2 text-center text-sm font-bold flex items-center justify-center gap-2 shadow-md"
            data-testid="admin-view-mode-banner"
          >
            <Eye className="w-4 h-4" />
            Mode lecture (agence MSC) — vous voyez le back-office comme le client. Modifications désactivées.
          </div>
        )}
        <div className={user?.view_mode === "client" ? "pointer-events-auto" : ""}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
